import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, RotateCcw, Maximize, X, Timer, Volume2, VolumeX, MousePointerClick, MonitorUp } from 'lucide-react';
import { SCENES, PRESENTATION, TOTAL_SECONDS } from '../presentationConfig';
import { useClock } from '../engine/useClock';
import { Stage, fmtClock } from '../engine/Stage';
import { usePreload, ReadyScreen } from '../engine/Preloader';
import { usePresentationKeys, toggleFullscreen } from '../engine/controls';
import { usePresenterBroadcast } from '../engine/presenterChannel';

export default function PresenterMode() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const autoplay = params.get('autoplay') === 'true';
  const startScene = Math.max(0, (parseInt(params.get('scene') || '1', 10) || 1) - 1);
  const preload = usePreload(true);
  const [began, setBegan] = useState(false);
  const [showTimer, setShowTimer] = useState(params.get('timer') !== 'false');
  const [audioOn, setAudioOn] = useState(PRESENTATION.audio.defaultOn);
  const [clickAdvance, setClickAdvance] = useState(PRESENTATION.clickToAdvance);
  const [controls, setControls] = useState(true);
  const idle = useRef(null);
  const clock = useClock({ scenes: SCENES, duration: PRESENTATION.sceneDuration, initialIndex: startScene, autoplay: false, speed: Number(params.get('speed')) || 1 });

  const begin = useCallback(() => {
    setBegan(true);
    clock.play();
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.().catch(() => {});
  }, [clock]);

  const exit = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    window.location.href = '/jackson-stops-presentation';
  }, []);

  const poke = useCallback(() => {
    setControls(true);
    clearTimeout(idle.current);
    idle.current = setTimeout(() => setControls(false), 3000);
  }, []);
  useEffect(() => { poke(); return () => clearTimeout(idle.current); }, [poke]);

  const keys = useMemo(() => ({
    next: clock.next, prev: clock.prev, toggle: clock.toggle, restart: clock.restart,
    escape: () => { if (!document.fullscreenElement) exit(); },
    fullscreen: () => toggleFullscreen(), timer: () => setShowTimer((v) => !v), audio: () => setAudioOn((v) => !v),
  }), [clock.next, clock.prev, clock.toggle, clock.restart, exit]);
  usePresentationKeys(keys, began);

  const scene = SCENES[clock.index];
  const nextScene = SCENES[clock.index + 1];
  const broadcastState = useMemo(() => ({
    index: clock.index, t: Math.floor(clock.t), playing: clock.playing, overall: Math.floor(clock.overall),
    title: scene.title, note: scene.note, next: nextScene?.title || 'End', began,
  }), [clock.index, clock.t, clock.playing, clock.overall, scene, nextScene, began]);
  usePresenterBroadcast(broadcastState, { next: clock.next, prev: clock.prev, toggle: clock.toggle, restart: clock.restart, goto: (i) => clock.goTo(i), begin });

  if (!began) {
    return <ReadyScreen preload={preload} title={PRESENTATION.title} subtitle={`${SCENES.length} scenes × ${PRESENTATION.sceneDuration}s · ${fmtClock(TOTAL_SECONDS)}`} onBegin={begin} autoBegin={autoplay} />;
  }

  return (
    <div className={`pk-present ${controls ? '' : 'pk-idle'}`} onMouseMove={poke} data-testid="pk-presenter-mode">
      <Stage scenes={SCENES} clock={clock} showTimer={showTimer} mode="present" audioOn={audioOn} onClick={() => { if (clickAdvance) clock.next(); }} />
      <div className={`pk-controls ${controls ? 'show' : ''}`} onClick={(e) => e.stopPropagation()} data-testid="pk-presenter-controls">
        <div className="pk-controls-info">
          <div className="pk-controls-now"><b>{String(clock.index + 1).padStart(2, '0')}</b> {scene.title}</div>
          <div className="pk-controls-next">Next · {nextScene ? nextScene.title : 'End — hold final frame'}</div>
        </div>
        <div className="pk-controls-buttons">
          <button onClick={clock.prev} title="Previous (←)" data-testid="pk-prev-btn"><ChevronLeft size={20} /></button>
          <button onClick={clock.toggle} title="Pause / resume (space)" className="pk-ctrl-main" data-testid="pk-toggle-btn">{clock.playing ? <Pause size={20} /> : <Play size={20} />}</button>
          <button onClick={clock.next} title="Next (→)" data-testid="pk-next-btn"><ChevronRight size={20} /></button>
          <span className="pk-ctrl-sep" />
          <button onClick={clock.restart} title="Restart (R)" data-testid="pk-restart-btn"><RotateCcw size={18} /></button>
          <button onClick={() => toggleFullscreen()} title="Fullscreen (F)" data-testid="pk-fullscreen-btn"><Maximize size={18} /></button>
          <button onClick={() => setShowTimer((v) => !v)} title="Timer (T)" className={showTimer ? 'on' : ''} data-testid="pk-timer-btn"><Timer size={18} /></button>
          <button onClick={() => setAudioOn((v) => !v)} title="Presentation audio (M)" className={audioOn ? 'on' : ''} data-testid="pk-audio-btn">{audioOn ? <Volume2 size={18} /> : <VolumeX size={18} />}</button>
          <button onClick={() => setClickAdvance((v) => !v)} title="Click to advance" className={clickAdvance ? 'on' : ''} data-testid="pk-click-advance-btn"><MousePointerClick size={18} /></button>
          <button onClick={() => window.open('/jackson-stops-presentation/presenter', 'pk-presenter', 'width=900,height=600')} title="Presenter window" data-testid="pk-presenter-window-btn"><MonitorUp size={18} /></button>
          <span className="pk-ctrl-sep" />
          <button onClick={exit} title="Exit (Esc)" data-testid="pk-exit-btn"><X size={18} /></button>
        </div>
        <div className="pk-controls-time">
          <span>{fmtClock(Math.max(0, PRESENTATION.sceneDuration - clock.t))}</span>
          <small>{fmtClock(clock.overall)} / {fmtClock(TOTAL_SECONDS)}</small>
        </div>
      </div>
    </div>
  );
}
